const CONVERSATION_ID_DELIMITER = '///';
const CONVERSATION_SID_PREFIX = `CONVERSATION_SERVICE_ENCRYPTED${CONVERSATION_ID_DELIMITER}`;

/**
 * Decodes standard or URL-safe Base64 to a binary string (best-effort).
 * Returns null if the payload is not valid Base64.
 */
const tryDecodeBase64ToBinaryString = (value: string): string | null => {
    try {
        const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
        const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
        return atob(padded);
    } catch {
        return null;
    }
};

/**
 * True when `value` is already the full MSGS wire-format conversation sid (Base64 of
 * `CONVERSATION_SERVICE_ENCRYPTED///` + inner token). GraphQL / SDK often return this form;
 * wrapping again would double-encode and cause backend decryption errors
 * (e.g. MissingKeyException for an absurd key version, see COMMSEXP-485).
 */
export const isCompleteConversationServiceEncryptedSid = (value: string): boolean => {
    if (!value) {
        return false;
    }
    const decoded = tryDecodeBase64ToBinaryString(value);
    return decoded != null && decoded.startsWith(CONVERSATION_SID_PREFIX);
};

/**
 * JS Messaging uses an encrypted conversation ID for the URL. This gets us the unencrypted conversation ID for usage with MSGGQL.
 */
export const getUnencryptedConversationId = (encryptedConversationId: string) => {
    const plainSid = atob(encryptedConversationId);
    const sidParts = plainSid.split(CONVERSATION_ID_DELIMITER);

    if (sidParts[0] !== 'CONVERSATION_SERVICE_ENCRYPTED' || sidParts.length !== 2) {
        throw new Error(`Invalid encrypted conversation id: ${encryptedConversationId}`);
    } else {
        return sidParts[1];
    }
};

/**
 * JS Messaging uses an encrypted conversation ID for the URL. This gets us the encrypted
 * conversation ID for usage with MSGGQL.
 *
 * This function is idempotent: if `conversationId` is already a complete wire-format sid
 * (see {@link isCompleteConversationServiceEncryptedSid}), it is returned unchanged. Without
 * this guard, callers that pass an already-wrapped value (the form returned from MSGGQL or
 * present in the URL) would produce a double-wrapped sid that the backend cannot decrypt
 * (see COMMSEXP-485).
 */
export const getEncryptedConversationId = (conversationId: string) => {
    if (isCompleteConversationServiceEncryptedSid(conversationId)) {
        return conversationId;
    }
    return btoa(`${CONVERSATION_SID_PREFIX}${conversationId}`);
};
